import { type AnalystLevel, type TeamJournal } from "@hakili/contracts";
import { t } from "@hakili/i18n";
import {
  Button,
  Callout,
  GuardedButton,
  Input,
  Modal,
  SegmentedControl,
  StatCard,
  Toast,
  ToastStack,
} from "@hakili/ui";
import { Users } from "lucide-react";
import { useState } from "react";

import type { TeamRead } from "../team-read.js";
import { DASH, fill } from "./dossiers.js";
import type { FichierBanque } from "./dossiers.js";
import { ModalCancel } from "./modal-cancel.js";
import { AbsenceDialog, RemoveDialog } from "./equipe-modals.js";
import { TeamTable } from "./equipe-table.js";
import { SkeletonRows } from "./skeleton-rows.js";
import { type GestureDoors, JournalCard, ReassignModal, TransferModal } from "./equipe-gestes.js";

/**
 * Round `L-5`, `M2` (`P3-BKW-003`'s phase-1 half, `P0-PRM-036`, WF-BNK-25), rebuilt by `C-2` on the kit and the shell:
 * **« Équipe », the chef's supervision screen** (D2b §2).
 *
 * Everything shown is the door's (`GET /api/v1/bank/team`): the members as `identity` keeps them, the open files, their
 * points and, since `C-2`'s first movement, **the open files by stage** , a closed map the server sums by the very query that
 * sums the points, so the bars are drawn from it and the screen derives none of it. The four tiles are counts and sums of that
 * one read (`R-3`), not a second source.
 *
 * The acts are the three the doors give: « Déclarer une absence » (`PATCH /api/v1/users/{id}/availability`, refusing `absent`
 * without a date), « Retirer de l'équipe » (`POST /api/v1/users/{id}/deactivate`, refused in words while open files remain , the
 * door's own sentence with its count), « Inviter un analyste » (`POST /api/v1/users`, within the chef's rights, I16). « Voir ses
 * dossiers (N) » opens the chef's pipeline filtered on the member (ruled, D2b question 2).
 *
 * **What has no door is not drawn** (D-1019-2, DX-011): Hakili's reading of the team, the quality table, the invitation's level (seniority
 * sits on the role, not the person) and the analytics' indicators are absent, not greyed and not dashed. « Rendre disponible » stays for
 * now: the server never ends an absence by itself, so removing the gesture would strand an analyst (R-2, awaiting the architect's and
 * the designer's ruling).
 *
 * **`EQUIPE-GESTURES`, round 2** gives the screen the three gestures the doors of round 1 opened (`equipe-gestes.tsx`): the unfolded
 * portfolio with « Réaffecter » on each file, « Transférer tout son portefeuille » on an absent analyst (and as a shortcut from the
 * departure's refusal), and the journal of the moves. They are drawn only where the doors are handed (`gestures`).
 */
export type { TeamMemberView, TeamRead } from "../team-read.js";

export interface TeamDoors {
  readonly onAbsence: (userId: string, returnsOn: string) => void;
  readonly onBack: (userId: string) => void;
  readonly onRemove: (userId: string) => void;
  readonly onInvite: (email: string, fullName: string, level: AnalystLevel) => void;
  /** The chef's pipeline filtered on one analyst (WF-BNK-24's filter). */
  readonly onFiles: (userId: string) => void;
}

/** The drawing's derivation of a name from a professional address: `prenom.nom@…` → « Prenom Nom ». */
export const nameFromEmail = (email: string): string =>
  (email.split("@")[0] ?? "")
    .split(/[._-]+/u)
    .filter((part) => part !== "")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

/** `SENIORITY-LEVEL` (D-1026-AA-1): the analyst's level, set once at the invite; the words are the drawing's (D2 0188ac18, frame 83). */
const LEVELS: readonly { readonly value: AnalystLevel; readonly key: Parameters<typeof t>[0] }[] = [
  { value: "junior", key: "bank.equipe.niveauJunior" },
  { value: "standard", key: "bank.equipe.niveauStandard" },
  { value: "senior", key: "bank.equipe.niveauSenior" },
];

function InviteModal(props: {
  readonly onClose: () => void;
  readonly onInvite: (email: string, fullName: string, level: AnalystLevel) => void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [level, setLevel] = useState<AnalystLevel>("standard");
  const name = nameFromEmail(email);
  const valid = name !== "" && email.includes("@");
  return (
    <Modal
      open
      title={t("bank.equipe.inviterTitre")}
      onClose={props.onClose}
      cross={false}
      size="lg"
      actions={
        <>
          <ModalCancel onClick={props.onClose} />
          {valid ? (
            <Button
              data-testid="equipe-inviter-envoyer"
              onClick={() => {
                props.onInvite(email.trim().toLowerCase(), name, level);
              }}
            >
              {t("bank.equipe.envoyerInvitation")}
            </Button>
          ) : (
            <GuardedButton disabled reason={t("bank.equipe.envoyerMotif")} testId="equipe-inviter-envoyer">
              {t("bank.equipe.envoyerInvitation")}
            </GuardedButton>
          )}
        </>
      }
    >
      <div data-testid="equipe-inviter">
        <p className="mb-3 text-sm text-gray-500" data-testid="equipe-inviter-phrase">
          {t("bank.equipe.inviterSousTitre")} {t("bank.equipe.inviterPhrase")}
        </p>
        <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="equipe-inviter-courriel">
          {t("bank.equipe.adresseProfessionnelle")}
        </label>
        <Input
          id="equipe-inviter-courriel"
          testId="equipe-inviter-courriel"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={t("bank.equipe.adresseExemple")}
        />
        <div className="mt-3">
          <span className="mb-1.5 block text-sm font-medium text-gray-700" id="equipe-inviter-niveau-label">
            {t("bank.equipe.niveau")}
          </span>
          <div data-testid="equipe-inviter-niveau">
            <SegmentedControl
              label={t("bank.equipe.niveau")}
              value={level}
              onChange={(next) => {
                setLevel(next as AnalystLevel);
              }}
              options={LEVELS.map((one) => ({
                value: one.value,
                label: t(one.key),
                testId: `equipe-inviter-niveau-${one.value}`,
              }))}
            />
          </div>
          <span className="mt-1.5 block text-sm text-gray-500" data-testid="equipe-inviter-niveau-aide">
            {t("bank.equipe.niveauAide")}
          </span>
        </div>
        {name === "" ? null : (
          <p className="mt-2 text-sm leading-relaxed text-gray-500" data-testid="equipe-inviter-entre">
            {fill("bank.equipe.inviterEntre", { nom: name })}
          </p>
        )}
      </div>
    </Modal>
  );
}

export function Equipe(props: {
  readonly read: TeamRead | null;
  /** `A47`: why the team could not be read, in words , a failed or mismatched read is never an endless « Chargement ». */
  readonly failure?: string | null;
  readonly busy: boolean;
  /** The door's refusal, by member (« Sortie impossible : … »). */
  readonly refusal: { readonly userId: string; readonly message: string } | null;
  readonly doors: TeamDoors;
  /**
   * `EQUIPE-GESTURES`, round 2: the chef's open files (the pipeline's own read, `analystUserId` on each), the journal of the moves and the
   * doors of the three gestures. Absent, the unfold, « Réaffecter », « Transférer tout son portefeuille » and the journal are not drawn:
   * a control without a door is not drawn (D-1019-2).
   */
  readonly files?: readonly FichierBanque[];
  readonly journal?: TeamJournal | null;
  readonly gestures?: GestureDoors;
  /** The toast after a gesture the door accepted (frame 89): a sentence the screen wrote, and its dismissal. */
  readonly notice?: string | null;
}): React.JSX.Element {
  const [absenceOpen, setAbsenceOpen] = useState<string | null>(null);
  const [returnsOn, setReturnsOn] = useState("");
  // `EQUIPE-MODALS`: the member whose removal dialog is open, and whether the door has been pressed from it (a refusal from before the
  // press is the row's, not the dialog's).
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeTried, setRemoveTried] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
  const [reassigning, setReassigning] = useState<FichierBanque | null>(null);
  const [transferring, setTransferring] = useState<{ readonly id: string; readonly name: string } | null>(
    null,
  );
  if (props.read === null) {
    return props.failure === undefined || props.failure === null ? (
      <div data-testid="bank-equipe-chargement">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">{t("bank.equipe.titre")}</h1>
          <p className="mt-1.5 text-sm text-gray-500">{t("bank.equipe.sousTitreC2")}</p>
        </div>
        <SkeletonRows rows={4} />
      </div>
    ) : (
      <Callout tone="bad" className="m-6" testId="equipe-lecture-impossible">
        {props.failure}
      </Callout>
    );
  }
  const { members, weights, self } = props.read;
  // The server hands a file only to an ACTIVE account (identity-api.service.ts:221; an invited one cannot sign in to take it):
  // the tiles count those, available or absent, and the invited account stays outside the denominator.
  const here = members.filter((one) => one.statut === "actif");
  const available = here.filter((one) => one.disponibilite !== "absent");
  const away = here.filter((one) => one.disponibilite === "absent");
  const tiles: readonly {
    readonly id: string;
    readonly label: string;
    readonly value: string;
    readonly sub: string;
    readonly hot?: boolean;
  }[] = [
    {
      id: "disponibles",
      label: t("bank.equipe.tuileDisponibles"),
      value: fill("bank.decisions.surN", { n: String(available.length), total: String(here.length) }),
      sub: t("bank.equipe.tuileDisponiblesSous"),
    },
    {
      id: "ouverts",
      label: t("bank.equipe.tuileOuverts"),
      // `CHEF-HELD-FILES` (frame 144): his files are in the team's pipeline, so they count here, and not in « Analystes disponibles ».
      value: String(members.reduce((total, one) => total + one.openFiles, 0) + (self?.openFiles ?? 0)),
      sub: t("bank.equipe.tuileOuvertsSous"),
    },
    {
      id: "charge",
      label: t("bank.equipe.tuileCharge"),
      value: String(members.reduce((total, one) => total + one.points, 0) + (self?.points ?? 0)),
      sub: t("bank.equipe.tuileChargeSous"),
    },
    {
      id: "indisponibles",
      label: t("bank.equipe.tuileIndisponibles"),
      value: String(away.length),
      sub: away.length > 0 ? t("bank.equipe.tuileIndisponiblesSous") : t("bank.equipe.personne"),
      ...(away.length > 0 ? { hot: true } : {}),
    },
  ];

  return (
    <div data-testid="bank-equipe">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("bank.equipe.titre")}</h1>
          <p className="mt-1.5 text-sm text-gray-500">{t("bank.equipe.sousTitreC2")}</p>
        </div>
        <Button
          variant="primary"
          data-testid="equipe-inviter-ouvrir"
          onClick={() => {
            setInviteOpen(true);
          }}
        >
          <Users className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
          {t("bank.equipe.inviter")}
        </Button>
      </div>

      {/* D2's `.tiles` (ui.js:20, `repeat(n, minmax(0,1fr))`; components.css:100): ONE row at every width, the 1099 px rule does not list it. */}
      <div className="mb-6 grid grid-cols-4 gap-2.5" data-testid="equipe-tuiles">
        {tiles.map((tile) => (
          <div key={tile.id} data-testid={`equipe-tuile-${tile.id}`}>
            <StatCard
              label={tile.label}
              value={tile.value}
              // D2's `.tile .s` (components.css:102): the sub-line is metadata, 13 px, ONE line, where it holds one line; the kit's own hint stays untouched.
              // The one that cannot (« points, pas un nombre de dossiers » at 960, D2 wraps it) keeps the kit's 14 px hint, which wraps: never a spill.
              hint={
                tile.id === "charge" ? (
                  tile.sub
                ) : (
                  <span className="block whitespace-nowrap text-[length:var(--hk-fs-meta)]" data-meta>
                    {tile.sub}
                  </span>
                )
              }
              {...(tile.hot === true ? { tone: "hot" as const } : {})}
            />
          </div>
        ))}
      </div>

      <TeamTable
        members={members}
        self={self}
        weights={weights}
        asOf={props.read.asOf}
        busy={props.busy}
        refusal={props.refusal}
        files={props.files ?? []}
        gestures={props.gestures !== undefined}
        unfolded={unfolded}
        onToggle={(memberId) => {
          setUnfolded((held) => {
            const next = new Set(held);
            if (next.has(memberId)) next.delete(memberId);
            else next.add(memberId);
            return next;
          });
        }}
        onFiles={props.doors.onFiles}
        onBack={props.doors.onBack}
        onAbsence={(memberId) => {
          setAbsenceOpen(memberId);
          setReturnsOn("");
        }}
        onRemove={(memberId) => {
          setRemoving(memberId);
          setRemoveTried(false);
        }}
        onTransfer={(member) => {
          setTransferring({ id: member.id, name: member.fullName });
        }}
        onReassign={setReassigning}
      />
      {props.gestures === undefined ? null : <JournalCard journal={props.journal ?? null} />}
      {props.notice === undefined || props.notice === null ? null : (
        <ToastStack>
          <Toast>
            <span data-testid="equipe-toast">{props.notice}</span>
          </Toast>
        </ToastStack>
      )}
      {props.gestures !== undefined && reassigning !== null ? (
        <ReassignModal
          file={reassigning}
          doors={props.gestures}
          onClose={() => {
            setReassigning(null);
          }}
        />
      ) : null}
      {absenceOpen !== null ? (
        <AbsenceDialog
          memberId={absenceOpen}
          memberName={members.find((one) => one.id === absenceOpen)?.fullName ?? DASH}
          text={returnsOn}
          today={props.read.asOf}
          onText={setReturnsOn}
          busy={props.busy}
          onConfirm={(day) => {
            props.doors.onAbsence(absenceOpen, day);
            setAbsenceOpen(null);
          }}
          onClose={() => {
            setAbsenceOpen(null);
          }}
        />
      ) : null}
      {removing !== null && members.find((one) => one.id === removing)?.statut !== "desactive" ? (
        <RemoveDialog
          memberId={removing}
          memberName={members.find((one) => one.id === removing)?.fullName ?? DASH}
          openFiles={members.find((one) => one.id === removing)?.openFiles ?? 0}
          busy={props.busy}
          refusal={
            removeTried && props.refusal !== null && props.refusal.userId === removing
              ? props.refusal.message
              : null
          }
          onTransfer={
            props.gestures === undefined
              ? undefined
              : () => {
                  const held = members.find((one) => one.id === removing);
                  setRemoving(null);
                  if (held !== undefined) setTransferring({ id: held.id, name: held.fullName });
                }
          }
          onConfirm={() => {
            setRemoveTried(true);
            props.doors.onRemove(removing);
          }}
          onClose={() => {
            setRemoving(null);
          }}
        />
      ) : null}
      {props.gestures !== undefined && transferring !== null ? (
        <TransferModal
          memberId={transferring.id}
          memberName={transferring.name}
          doors={props.gestures}
          onClose={() => {
            setTransferring(null);
          }}
        />
      ) : null}
      {inviteOpen ? (
        <InviteModal
          onClose={() => {
            setInviteOpen(false);
          }}
          onInvite={(email, fullName, level) => {
            props.doors.onInvite(email, fullName, level);
            setInviteOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
